import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { filterShipmentLocations } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { useAuth } from '../../context/AuthContext.js';
import { IndianRupee, Camera } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';

const AddExpense = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isCompanyAgent = user?.role === 'Agent' && !user?.party_id;
  const isHubAdmin = user?.role === 'Hub Admin';
  const forcePaidStatus = false;
  const isHubLocked = isCompanyAgent && Boolean(user?.location_hub_id);
  const [formData, setFormData] = useState({
    category: 'rent',
    custom_category: '',
    expense_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    vendor_name: '',
    bill_number: '',
    remarks: '',
    status: 'pending',
    payment_mode: 'cash',
    transaction_id: '',
    paid_to: '',
    location_hub_id: '',
  });
  
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    category:     (v) => validators.select(v, 'category'),
    expense_date: (v) => validators.required(v, 'Expense date'),
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    description:  (v) => validators.required(v, 'Description'),
    location_hub_id: (v) => validators.select(v, 'location hub'),
  };

  const selectedHub = locationHubs.find(
    (h) => String(h.id) === String(formData.location_hub_id)
  );

  const paymentModes = ['cash', 'upi'];

  useEffect(() => {
    fetchDropdownData();
    setFormData(prev => ({
      ...prev,
      location_hub_id: user?.location_hub_id ? String(user.location_hub_id) : prev.location_hub_id,
      status: 'pending',
      payment_mode: prev.payment_mode || 'cash',
    }));
  }, [user?.location_hub_id, forcePaidStatus]);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, hubsRes] = await Promise.all([
        expenseService.getCategories(),
        locationHubService.getHubsForSelect({ brief: 1 }),
      ]);
      setCategories(categoriesRes.data || []);
      const hubs = filterShipmentLocations(hubsRes.data?.hubs || []);
      setLocationHubs(hubs);
    } catch (err) {
      Alert.error('Failed to load dropdown data');
      console.error('Failed to load dropdown data', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setFormData((prev) => ({
      ...prev,
      [name]: filtered,
      ...(name === 'status' && filtered === 'paid' && !prev.payment_mode ? { payment_mode: 'cash' } : {}),
    }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleFileChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setFileAttachment(file);
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setFilePreview('pdf');
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const fileInput = document.getElementById('file-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const categoryValue =
      formData.category === 'other'
        ? (formData.custom_category || '').trim().toLowerCase().replace(/\s+/g, '_')
        : formData.category;

    const payload = { ...formData, category: categoryValue || 'other' };
    delete payload.custom_category;
    delete payload.month;
    delete payload.year;

    if (!validate({ ...payload, category: categoryValue || formData.category }, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }
    if (formData.category === 'other' && !categoryValue) {
      Alert.error('Please enter a custom category');
      return;
    }

    setLoading(true);

    try {
      await expenseService.create(payload, fileAttachment);
      Alert.success('Expense created successfully!');
      setTimeout(() => navigate('/expenses'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const isPaidStatus = formData.status === 'paid';

  return (
    <DashboardLayout>
              <div className="w-full min-w-0 max-w-full mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <IndianRupee className="w-5 h-5 text-buttonBg" /> Add New Expense
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  
                  {/* Section 1: Basic Expense Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Category <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="category"
                          value={formData.category === 'other' || !categories.some((c) => c.value === formData.category) ? 'other' : formData.category}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'other') {
                              setFormData((prev) => ({ ...prev, category: 'other' }));
                            } else {
                              setFormData((prev) => ({ ...prev, category: v, custom_category: '' }));
                            }
                          }}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.category ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          {categories.filter((c) => c.value !== 'other').map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                          <option value="other">Other</option>
                        </SelectField>
                        {(formData.category === 'other' || formData.custom_category) && (
                          <input
                            type="text"
                            name="custom_category"
                            value={formData.custom_category || ''}
                            onChange={(e) => setFormData((prev) => ({
                              ...prev,
                              custom_category: e.target.value,
                              category: 'other',
                            }))}
                            placeholder="Enter category"
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
                          />
                        )}
                        <ValidationMessage error={errors.category} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Expense Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="expense_date"
                          value={formData.expense_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.expense_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.expense_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Amount (₹) <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="Enter amount"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.amount ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.amount} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location Hub Name <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="location_hub_id"
                          value={formData.location_hub_id}
                          onChange={handleChange}
                          disabled={isHubLocked}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location_hub_id ? 'border-red-400' : 'border-gray-300'} ${isHubLocked ? 'bg-gray-100' : ''}`}
                          required
                        >
                          <option value="">Select location hub</option>
                          {sortLocationsAsc(locationHubs).map((hub) => (
                            <option key={hub.id} value={hub.id}>
                              {formatLocationDropdownLabel(hub)}
                            </option>
                          ))}
                        </SelectField>
                        {isHubLocked && (
                          <p className="text-[11px] text-gray-500 mt-1">Locked to your assigned hub</p>
                        )}
                        <ValidationMessage error={errors.location_hub_id} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Description <span className="text-danger font-bold">*</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter expense description"
                          rows="1"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.description ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.description} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                    {/* Section 2: Vendor Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Vendor Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Vendor Name
                          </label>
                          <input
                            type="text"
                            name="vendor_name"
                            value={formData.vendor_name}
                            onChange={handleChange}
                            placeholder="Enter vendor name"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Bill Number
                          </label>
                          <input
                            type="text"
                            name="bill_number"
                            value={formData.bill_number}
                            onChange={handleChange}
                            placeholder="Enter bill number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600">This expense is submitted as pending until an admin or accountant approves or rejects it.</p>
                  </div>

                  {/* Section 4: File Attachment & Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          File Attachment
                        </label>
                        <MediaCaptureField mode="document" onPick={handleFileChange} />
                        {filePreview === "pdf" ? (
                          <p className="text-xs text-gray-500 mt-1">PDF attached</p>
                        ) : filePreview ? (
                          <img src={filePreview} alt="Preview" className="mt-2 w-16 h-16 object-cover rounded-lg border border-gray-200" />
                        ) : null}

<p className="text-xs text-gray-600 mt-1">
  Accepted formats: JPG, PNG, GIF, WebP, PDF (Max 5MB)
</p>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Enter any additional remarks"
                          rows="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button
                      variant="primary"
                      type="submit"
                      loading={loading}
                    >
                      {loading ? 'Creating...' : 'Create Expense'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/expenses')}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddExpense;